import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useOrganization } from '../../contexts/OrganizationContext';
import { FiEdit2, FiTrash2, FiMail, FiUserPlus } from 'react-icons/fi';
import InviteUser from './InviteUser';
import Select from 'react-select';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'employee';
  department_id: string | null;
  department_name: string | null;
  registration_status?: 'pending' | 'complete';
}

interface Department {
  id: string;
  name: string;
  sub_departments?: Department[];
}

interface UserListProps {
  departmentId?: string;
}

export default function UserList({ departmentId }: UserListProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentContext } = useOrganization();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [filters, setFilters] = useState({
    role: null as string | null,
    department: departmentId || null as string | null,
    status: null as string | null,
    search: ''
  });
  const [adminCount, setAdminCount] = useState(0);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [newlyInvitedUsers, setNewlyInvitedUsers] = useState<Set<string>>(new Set());

  const fetchAllUsers = async () => {
    if (!currentContext.organization_id) return;
    
    try {
      const response = await axios.get(`/api/organizations/${currentContext.organization_id}/users`);
      setAllUsers(response.data.users);
      setUsers(response.data.users);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    }
  };

  const fetchDepartments = async () => {
    if (!currentContext.organization_id) return;
    
    try {
      const response = await axios.get(`/api/organizations/${currentContext.organization_id}/departments`);
      setDepartments(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch departments');
    }
  };

  useEffect(() => {
    fetchAllUsers();
    fetchDepartments();
  }, [currentContext.organization_id]);

  // Update filters when departmentId changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      department: departmentId || null
    }));
  }, [departmentId]);

  // Filter users based on all filters
  useEffect(() => {
    let filtered = allUsers;
    
    if (filters.role) {
      filtered = filtered.filter(user => user.role === filters.role);
    }
    if (filters.department) {
      filtered = filtered.filter(user => user.department_id === filters.department);
    }
    if (filters.status) {
      filtered = filtered.filter(user => user.registration_status === filters.status);
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    }
    
    setUsers(filtered);
  }, [filters, allUsers]);

  useEffect(() => {
    // Get current user ID from context or auth
    const userId = localStorage.getItem('userId'); // Adjust based on your auth setup
    setCurrentUserId(userId);
  }, []);

  useEffect(() => {
    // Count admins in the organization
    const count = users.filter(user => user.role === 'admin').length;
    setAdminCount(count);
  }, [users]);

  const fetchDepartmentUsers = async () => {
    if (!currentContext.organization_id || !departmentId) return;
    
    try {
      setIsLoading(true);
      const response = await axios.get(`/api/organizations/${currentContext.organization_id}/departments/${departmentId}/users`);
      setUsers(response.data.users);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'admin' | 'manager' | 'employee') => {
    if (!currentContext.organization_id) return;

    // Check if this is the last admin
    if (adminCount === 1 && users.find(u => u.id === userId)?.role === 'admin' && newRole !== 'admin') {
      alert('Cannot remove the last admin from the organization.');
      return;
    }

    // Check if user is changing their own role
    if (userId === currentUserId) {
      const isBecomingAdmin = newRole === 'admin';
      const isRemovingAdmin = users.find(u => u.id === userId)?.role === 'admin' && newRole !== 'admin';
      
      if (isBecomingAdmin) {
        if (!window.confirm('You are about to make yourself an admin. This will give you full access to the organization. Continue?')) {
          return;
        }
      } else if (isRemovingAdmin) {
        if (!window.confirm('You are about to remove your admin privileges. This will affect your access to the organization. Continue?')) {
          return;
        }
      }
    }

    try {
      await axios.put(`/api/organizations/${currentContext.organization_id}/users/${userId}`, {
        role: newRole
      });
      
      // Update the selected user if it's the one being edited
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser(prev => prev ? { ...prev, role: newRole } : null);
      }
      
      fetchAllUsers();
      if (departmentId) {
        fetchDepartmentUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user role');
    }
  };

  const handleDepartmentChange = async (userId: string, newDepartmentId: string | null) => {
    if (!currentContext.organization_id) return;

    try {
      await axios.put(`/api/organizations/${currentContext.organization_id}/users/${userId}`, {
        department_id: newDepartmentId
      });
      
      // Update the selected user if it's the one being edited
      if (selectedUser && selectedUser.id === userId) {
        const departmentName = newDepartmentId ? departments.find(d => d.id === newDepartmentId)?.name || null : null;
        setSelectedUser(prev => prev ? { 
          ...prev, 
          department_id: newDepartmentId,
          department_name: departmentName
        } : null);
      }
      
      fetchAllUsers();
      if (departmentId) {
        fetchDepartmentUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user department');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!currentContext.organization_id) return;

    if (!window.confirm('Are you sure you want to remove this user from the department?')) {
      return;
    }

    try {
      await axios.delete(`/api/organizations/${currentContext.organization_id}/departments/${departmentId}/users/${userId}`);
      fetchAllUsers();
      if (departmentId) {
        fetchDepartmentUsers();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove user');
    }
  };

  const handleBulkAction = async (action: 'role' | 'department', value: string) => {
    if (!currentContext.organization_id || selectedUsers.length === 0) return;

    // Check if any selected users are admins
    const selectedAdmins = users.filter(u => selectedUsers.includes(u.id) && u.role === 'admin');
    if (selectedAdmins.length > 0 && value !== 'admin') {
      if (adminCount === selectedAdmins.length) {
        alert('Cannot remove all admins from the organization.');
        return;
      }
      if (!window.confirm(`You are about to change the role of ${selectedAdmins.length} admin(s). This will affect their access to the organization. Continue?`)) {
        return;
      }
    }

    try {
      await Promise.all(selectedUsers.map(userId => {
        if (action === 'role') {
          return handleRoleChange(userId, value as 'admin' | 'manager' | 'employee');
        } else {
          return handleDepartmentChange(userId, value);
        }
      }));
      setSelectedUsers([]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to perform bulk action');
    }
  };

  const buildDepartmentOptions = (deps: Department[], level = 0): { value: string; label: string }[] => {
    return deps.flatMap(dept => [
      { value: dept.id, label: '  '.repeat(level) + dept.name },
      ...(dept.sub_departments ? buildDepartmentOptions(dept.sub_departments, level + 1) : [])
    ]);
  };

  const getDepartmentLabel = (deptId: string | null): string => {
    if (!deptId) return 'All Departments';
    
    const findDepartment = (deps: Department[], targetId: string): string | null => {
      for (const dept of deps) {
        if (dept.id === targetId) return dept.name;
        if (dept.sub_departments) {
          const found = findDepartment(dept.sub_departments, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    return findDepartment(departments, deptId) || 'Unknown Department';
  };

  const handleUserInvited = async (newUserData?: any) => {
    try {
      // Only do optimistic update if we have user data (true success case)
      if (newUserData) {
        const tempId = `temp_${Date.now()}`;
        const optimisticUser: User = {
          id: tempId,
          name: newUserData.name || 'Pending Registration',
          email: newUserData.email,
          role: newUserData.role || 'employee',
          department_id: newUserData.department_id || null,
          department_name: newUserData.department_name || null,
          registration_status: 'pending'
        };
        
        setAllUsers(prev => [...prev, optimisticUser]);
        setUsers(prev => [...prev, optimisticUser]);
        setNewlyInvitedUsers(prev => new Set([...prev, tempId]));
        
        // Remove highlight after 5 seconds
        setTimeout(() => {
          setNewlyInvitedUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(tempId);
            return newSet;
          });
        }, 5000);
      }
      
      // Show loading state while fetching fresh data
      setIsLoading(true);
      
      // Fetch the latest data from the server
      await fetchAllUsers();
      if (departmentId) {
        await fetchDepartmentUsers();
      }
    } catch (err: any) {
      console.error('Error updating user list after invitation:', err);
      // If optimistic update failed, still try to fetch fresh data
      try {
        await fetchAllUsers();
        if (departmentId) {
          await fetchDepartmentUsers();
        }
      } catch (fetchErr) {
        console.error('Error fetching users after invitation:', fetchErr);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendInvite = async (userId: string) => {
    if (!currentContext.organization_id) return;

    try {
      await axios.post(`/api/organizations/${currentContext.organization_id}/users/${userId}/resend-invite`);
      alert('Invitation email has been resent successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to resend invitation');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <style>
        {`
          .select-container {
            position: relative;
          }
          .select-container .select__control {
            z-index: 1;
          }
          .select-container .select__menu {
            z-index: 1000;
          }
          .modal {
            z-index: 2000;
          }
          .modal .select-container .select__menu {
            z-index: 2002;
          }
        `}
      </style>
      <div className="p-4 border-b">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold">Users</h2>
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <FiUserPlus className="mr-2" />
            Invite User
          </button>
        </div>
      </div>

      {showInviteModal && (
        <InviteUser
          onUserInvited={(userData, shouldClose) => {
            handleUserInvited(userData);
            // Close modal if shouldClose is true
            if (shouldClose) {
              setShowInviteModal(false);
            }
          }}
          organizationId={currentContext.organization_id || ''}
        />
      )}

      <div className="p-4">
        {error && (
          <div className="mb-4 text-red-500">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              placeholder="Search users..."
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <div className="select-container">
              <Select
                value={filters.role ? { value: filters.role, label: filters.role.charAt(0).toUpperCase() + filters.role.slice(1) } : null}
                onChange={(option) => setFilters(prev => ({ ...prev, role: option?.value || null }))}
                options={[
                  { value: 'admin', label: 'Admin' },
                  { value: 'manager', label: 'Manager' },
                  { value: 'employee', label: 'Employee' }
                ]}
                isClearable
                className="w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
            <div className="select-container">
              <Select
                value={filters.department ? { 
                  value: filters.department, 
                  label: getDepartmentLabel(filters.department)
                } : null}
                onChange={(option) => setFilters(prev => ({ ...prev, department: option?.value || null }))}
                options={[
                  { value: '', label: 'All Departments' },
                  ...buildDepartmentOptions(departments)
                ]}
                isClearable
                className="w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <div className="select-container">
              <Select
                value={filters.status ? { value: filters.status, label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1) } : null}
                onChange={(option) => setFilters(prev => ({ ...prev, status: option?.value || null }))}
                options={[
                  { value: 'complete', label: 'Complete' },
                  { value: 'pending', label: 'Pending' }
                ]}
                isClearable
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{selectedUsers.length} users selected</span>
                <button
                  onClick={() => setSelectedUsers([])}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Clear Selection
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="select-container">
                  <Select
                    placeholder="Change Role"
                    onChange={(option) => option && handleBulkAction('role', option.value)}
                    options={[
                      { value: 'admin', label: 'Admin' },
                      { value: 'manager', label: 'Manager' },
                      { value: 'employee', label: 'Employee' }
                    ]}
                  />
                </div>
                <div className="select-container">
                  <Select
                    placeholder="Move to Department"
                    onChange={(option) => option && handleBulkAction('department', option.value)}
                    options={[
                      { value: '', label: 'No Department' },
                      ...buildDepartmentOptions(departments)
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(users.map(u => u.id));
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr 
                  key={user.id} 
                  className={`hover:bg-gray-50 ${
                    newlyInvitedUsers.has(user.id) ? 'bg-green-50 border-l-4 border-green-400' : ''
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers(prev => [...prev, user.id]);
                        } else {
                          setSelectedUsers(prev => prev.filter(id => id !== user.id));
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="select-container">
                      <Select
                        value={{ value: user.role, label: user.role.charAt(0).toUpperCase() + user.role.slice(1) }}
                        onChange={(option) => option && handleRoleChange(user.id, option.value as 'admin' | 'manager' | 'employee')}
                        options={[
                          { value: 'admin', label: 'Admin' },
                          { value: 'manager', label: 'Manager' },
                          { value: 'employee', label: 'Employee' }
                        ]}
                        className="w-32"
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.registration_status === 'complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {user.registration_status || 'complete'}
                      </span>
                      {newlyInvitedUsers.has(user.id) && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                          New
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowUserModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      {user.registration_status === 'pending' && (
                        <button
                          onClick={() => handleResendInvite(user.id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <FiMail className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveUser(user.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 modal">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Edit User</h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <div className="mt-1 text-sm text-gray-900">{selectedUser.name}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <div className="mt-1 text-sm text-gray-900">{selectedUser.email}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <div className="mt-1">
                    <div className="select-container">
                      <Select
                        value={{ value: selectedUser.role, label: selectedUser.role.charAt(0).toUpperCase() + selectedUser.role.slice(1) }}
                        onChange={(option) => option && handleRoleChange(selectedUser.id, option.value as 'admin' | 'manager' | 'employee')}
                        options={[
                          { value: 'admin', label: 'Admin' },
                          { value: 'manager', label: 'Manager' },
                          { value: 'employee', label: 'Employee' }
                        ]}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <div className="mt-1">
                    <div className="select-container">
                      <Select
                        value={selectedUser.department_id ? { value: selectedUser.department_id, label: selectedUser.department_name } : null}
                        onChange={(option) => handleDepartmentChange(selectedUser.id, option?.value || null)}
                        options={[
                          { value: '', label: 'No Department' },
                          ...buildDepartmentOptions(departments)
                        ]}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 